import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Loader2, RefreshCw, Unlink } from "lucide-react";
import { format } from "date-fns";
import {
  useGoogleCalendarStatus,
  useGoogleCalendarConnect,
  useGoogleCalendarDisconnect,
  useGoogleCalendarToggleSync,
  useGoogleCalendarSync,
} from "@/hooks/useGoogleCalendar";

export const GoogleCalendarConnect = () => {
  const { data: status, isLoading } = useGoogleCalendarStatus();
  const { connect } = useGoogleCalendarConnect();
  const disconnect = useGoogleCalendarDisconnect();
  const toggleSync = useGoogleCalendarToggleSync();
  const sync = useGoogleCalendarSync();

  const connected = status?.connected;
  const conn = status?.connection;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Google Calendar</CardTitle>
            <CardDescription>
              Sync events between CommandX and Google Calendar in real time.
            </CardDescription>
          </div>
          {!isLoading && (
            <Badge
              variant="outline"
              className={
                connected
                  ? "bg-primary/15 text-primary border-primary/30"
                  : "bg-muted text-muted-foreground border-border"
              }
            >
              {connected ? "Connected" : "Disconnected"}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking connection…
          </div>
        ) : connected && conn ? (
          <>
            <p className="text-sm text-muted-foreground">
              Connected as <span className="font-medium text-foreground">{conn.google_email}</span>
            </p>
            {conn.last_sync_at && (
              <p className="text-xs text-muted-foreground">
                Last synced: {format(new Date(conn.last_sync_at), "PPpp")}
              </p>
            )}

            <div className="flex items-center justify-between max-w-lg">
              <div>
                <p className="text-sm font-medium">Auto-sync enabled</p>
                <p className="text-xs text-muted-foreground">
                  {conn.sync_enabled
                    ? "Events sync automatically between calendars"
                    : "Sync is paused — changes won't propagate"}
                </p>
              </div>
              <Switch
                checked={conn.sync_enabled}
                disabled={toggleSync.isPending}
                onCheckedChange={(checked) => toggleSync.mutate(checked)}
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => sync.mutate("full-sync")}
                disabled={sync.isPending}
              >
                {sync.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1" />
                )}
                Sync Now
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => disconnect.mutate()}
                disabled={disconnect.isPending}
              >
                {disconnect.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                )}
                <Unlink className="h-4 w-4 mr-1" />
                Disconnect
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Connect your Google account to sync calendar events bidirectionally.
              Events created, edited, or deleted on either side will automatically reflect on the other.
            </p>
            <Button onClick={connect}>
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Connect Google Calendar
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
